import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ApiError } from '@services/apiClient'
import { sessionStore } from '@features/auth/services/sessionStore'
import { loadSendList, type SendFlowDeps } from '../services/sendFlow'
import { type SendItem } from '../types'

/**
 * Send 一覧画面の state machine + filter (T-D-Send)。
 *
 * 設計判断:
 *   - filter: 'all' | 'text' | 'file' | 'expired'。
 *     'expired' は expiresAt 超過 または accessCount >= maxAccess のアイテム。
 *   - error 文言は i18n key を返す (UI で t() を当てる)。
 */

export type SendFilter = 'all' | 'text' | 'file' | 'expired'

const nowIso = (): string => new Date().toISOString()

const isInactive = (item: SendItem): boolean => {
  if (item.expiresAt !== null && item.expiresAt <= nowIso()) return true

  return item.maxAccess > 0 && item.accessCount >= item.maxAccess
}

export type SendListState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: readonly SendItem[]; failureCount: number }
  | { kind: 'error'; messageKey: string }

const ERROR_KEYS = {
  no_session: 'send.error.no_session',
  network: 'send.error.network',
  generic: 'send.error.generic',
} as const

const messageKeyFor = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return ERROR_KEYS.no_session

    return ERROR_KEYS.network
  }

  return ERROR_KEYS.generic
}

const applyFilter = (items: readonly SendItem[], filter: SendFilter): readonly SendItem[] => {
  if (filter === 'all') return items
  if (filter === 'expired') return items.filter(isInactive)

  return items.filter(item => item.payload.type === filter)
}

export interface UseSendList {
  state: SendListState
  filter: SendFilter
  setFilter: (f: SendFilter) => void
  filtered: readonly SendItem[]
  counts: { all: number; text: number; file: number; expired: number }
  reload: () => Promise<void>
}

export const useSendList = (flowDeps?: SendFlowDeps): UseSendList => {
  const [state, setState] = useState<SendListState>({ kind: 'loading' })
  const [filter, setFilter] = useState<SendFilter>('all')
  const mountedRef = useRef(true)

  const fetchOnce = useCallback(async (): Promise<void> => {
    const session = sessionStore.getState()
    if (session.kind !== 'unlocked') {
      if (mountedRef.current) {
        setState({ kind: 'error', messageKey: ERROR_KEYS.no_session })
      }

      return
    }

    try {
      const result = await loadSendList(session.symmetricKey, flowDeps)
      if (!mountedRef.current) return
      setState({ kind: 'ready', items: result.items, failureCount: result.failureCount })
    } catch (err) {
      if (!mountedRef.current) return
      setState({ kind: 'error', messageKey: messageKeyFor(err) })
    }
  }, [flowDeps])

  useEffect(() => {
    mountedRef.current = true
    void fetchOnce()

    return () => {
      mountedRef.current = false
    }
  }, [fetchOnce])

  const items = useMemo<readonly SendItem[]>(
    () => (state.kind === 'ready' ? state.items : []),
    [state],
  )

  const filtered = useMemo(() => applyFilter(items, filter), [items, filter])

  const counts = useMemo(
    () => ({
      all: items.length,
      text: items.filter(i => i.payload.type === 'text').length,
      file: items.filter(i => i.payload.type === 'file').length,
      expired: items.filter(isInactive).length,
    }),
    [items],
  )

  return { state, filter, setFilter, filtered, counts, reload: fetchOnce }
}
